<template>
  <div v-if="list.length" ref="slideshowRef" class="v-slideshow" :class="[wrapClass, { 'is-focus': ui.isFocus }]">
    <div class="v-slideshow-content">
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions, vuejs-accessibility/click-events-have-key-events -->
      <div
        ref="slideshowWrapper"
        :class="['v-slideshow-wrapper', { 'is-swiping': touch.isSwipingOnMobile }]"
        @mousedown.passive="(e) => touch.scrollerTouchStart(e, true)"
        @mouseup.passive="(e) => touch.scrollerTouchEnd(e, true)"
        @mousemove.passive="(e) => touch.scrollerTouchMove(e, true)"
        @touchstart="touch.scrollerTouchStart"
        @touchmove="touch.scrollerTouchMove"
        @touchend="touch.scrollerTouchEnd"
        @click.capture="touch.preventClickAfterDrag"
        @keydown.enter="touch.preventClickAfterDrag"
        @keydown.space.prevent="touch.preventClickAfterDrag"
      >
        <div ref="scroller" :style="scrollerStyle" class="v-scroller">
          <ul class="grid-x v-slideshow" aria-label="slideshow">
            <ClientOnly>
              <li
                v-for="(item, index) in list"
                :id="`slide-${currentPage}`"
                :key="index"
                class="cell small-12 slideshow-item"
                data-slideshow-item
                :class="{
                  active: currentPage === index + 1 || prevIndex === index + 1,
                  'padding-right-xxs': isPeekActive,
                  'stylitics-shop-similar': index === 0,
                }"
                :style="slideItemStyle"
                v-bind="{
                  ...(ui.currentIndicator === index && {
                    'aria-live': 'polite',
                    'aria-roledescription': list[ui.currentIndicator]?.title,
                  }),
                }"
                :aria-atomic="ui.currentIndicator === index"
                role="group"
                :inert="index + 1 !== currentPage || undefined"
              >
                <slot name="item" :data="item" :index="index" />
              </li>
              <template #fallback>
                <li
                  class="active cell small-12 slideshow-item"
                  data-slideshow-item
                  :class="{
                    'padding-right-xxs': isPeekActive,
                  }"
                  :style="slideItemStyle"
                >
                  <slot name="item" :data="list[0]" :index="0" />
                </li>
              </template>
            </ClientOnly>
          </ul>
        </div>
        <slot name="overlay" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch, onMounted, onBeforeUnmount, onBeforeUpdate, ref, reactive, nextTick, toRef, inject } from 'vue';
import { useImageryState } from '~/composables/useImageryState';
import useSlideshowAnimation, { NEXT_IMAGE_PEAK } from '~/composables/slideshow/useSlideshowAnimation';
import useSlideshowTouch from '~/composables/slideshow/useSlideshowTouch';
import useSlideshowNavigation from '~/composables/slideshow/useSlideshowNavigation';
import useSlideshowAutoplay from '~/composables/slideshow/useSlideshowAutoplay';
import { useDeviceType } from '~/composables/utils/useDeviceType';

const props = defineProps({
  list: {
    type: Array,
    required: true,
  },
  autoplayMode: {
    type: String,
    default: 'auto',
    validator: (val) => ['auto', 'manual', 'manual-hover'].includes(val),
  },
  backToFirst: {
    type: Boolean,
    default: false,
  },
  autoplayInterval: {
    type: Number,
    default: 5000,
    validator(value) {
      return value >= 800;
    },
  },
  thresholdLimit: {
    type: Number,
    default: 0.5,
    validator(value) {
      return value >= 0 && value <= 1;
    },
  },
  wrapClass: {
    type: String,
    default: '',
  },
  inOverlay: {
    type: Boolean,
    default: false,
  },
  initialIndex: {
    type: Number,
    default: 1,
  },
  topLevelZoomerMobile: {
    type: Boolean,
    default: false,
  },
  isAutoplayed: {
    type: Boolean,
    default: false,
  },
});

/**
 * @typedef {Object} SlideshowEmits
 * @property {(eventName: 'slideshow-change', ...args: any[]) => void} slideshow-change
 * @property {(eventName: 'load', ...args: any[]) => void} load
 * @property {(eventName: 'update-first-alt-dot', ...args: any[]) => void} update-first-alt-dot
 */

/**
 * @type {(eventName: 'slideshow-change' | 'load' | 'update-first-alt-dot', ...args: any[]) => void}
 */
const emit = defineEmits(['slideshow-change', 'load', 'update-first-alt-dot', 'first-slide-toggle']);
const { isMobile } = useDeviceType();
const { isOverlayOpen, currentPage: page, isZoomedIn } = useImageryState();

const triggerAltImageClickOnce = inject('triggerAltImageClickOnce', () => {});
const triggerLastAltImageClickOnce = inject('triggerLastAltImageClickOnce', () => {});

const currentPage = ref(props.initialIndex);
const numberOfPages = ref(1);
const prevIndex = ref(-1);

const slideshowRef = ref(null);
const slideshowWrapper = ref(null);
const scroller = ref(null);
const isMounted = ref(false);

const noTransition = ref(false);
const sliding = ref(false);
const scrollerX = ref(0);
const scrollerWidth = ref(null);
const hasBeenDragged = ref(false);

const ui = reactive({
  isFocus: false,
  currentIndicator: '',
  hoverState: false,
  buttonFocus: false,
});

const dimensions = reactive({
  childWidth: 0,
  wrapWidth: null,
  itemsLength: 3,
});

const ro = ref(null);

const navigation = useSlideshowNavigation({
  noTransition,
  sliding,
  scrollerX,
  hasBeenDragged,
  currentPage,
  numberOfPages,
  prevIndex,
  dimensions,
  scroller,
  emit,
});

const autoplay = useSlideshowAutoplay(props.autoplayInterval, navigation.move);

const { init, scrollerStyle, doSizing } = useSlideshowAnimation({
  currentPage,
  numberOfPages,
  dimensions,
  slideshowRef,
  slideshowWrapper,
  noTransition,
  sliding,
  scrollerX,
  scrollerWidth,
  scroller,
  emit,
});

const isPeekActive = computed(() => isMobile.value && props.list.length > 1);

const slideItemStyle = computed(() => {
  if (dimensions.childWidth) {
    return { width: `${dimensions.childWidth}px` };
  }
  return {
    width: isPeekActive.value ? `${NEXT_IMAGE_PEAK * 100}%` : '100%',
  };
});

const touch = useSlideshowTouch({
  currentPage,
  prevIndex,
  hasBeenDragged,
  scrollerX,
  scrollerWidth,
  dimensions,
  navigation,
  emit,
  autoplayMode: props.autoplayMode,
  autoplay,
  flags: {
    topLevelZoomerMobile: toRef(props, 'topLevelZoomerMobile'),
    thresholdLimit: props.thresholdLimit,
  },
  isZoomedIn,
  numberOfPages,
});

watch(
  () => props.list,
  (newList, oldList) => {
    if (newList !== oldList) {
      navigation?.change(1);
    }
  },
);

// Threshold (25% of slide width) to determine if the first slide is still considered active while scrolling
const FIRST_SLIDE_SCROLL_THRESHOLD = 0.25;

const isFirstSlideActive = computed(() => {
  if (currentPage.value !== 1 && currentPage.value !== 2) return false;
  if (!dimensions.childWidth) return currentPage.value === 1;

  const scrollDistance = Math.abs(scrollerX.value);
  const maxAllowedScroll = dimensions.childWidth * FIRST_SLIDE_SCROLL_THRESHOLD;

  return scrollDistance < maxAllowedScroll;
});

watch(
  isFirstSlideActive,
  (newValue) => {
    emit('first-slide-toggle', newValue);
  },
  { immediate: true },
);

watch(
  () => currentPage.value,
  () => {
    triggerAltImageClickOnce();
    if (currentPage.value === numberOfPages.value) {
      triggerLastAltImageClickOnce();
    }
  },
);

watch(isOverlayOpen, (newState, oldState) => {
  if (newState !== oldState && !props.inOverlay && isMobile.value) {
    navigation?.change(page.value);
    nextTick(() => {
      doSizing();
    });
  }
});

watch(
  () => props.isAutoplayed,
  (newVal) => {
    if (newVal) {
      autoplay.start();
    } else {
      autoplay.stop();
    }
  },
);

onMounted(() => {
  isMounted.value = true;
  document.addEventListener('mouseup', touch.documentMouseUp);
  document.addEventListener('mousemove', touch.documentMouseMove);

  if (props.list.length) {
    ro.value = new ResizeObserver(() => init(props.list.length));
    ro.value.observe(slideshowRef.value);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('mouseup', touch.documentMouseUp);
  document.removeEventListener('mousemove', touch.documentMouseMove);
  if (ro.value) {
    ro.value.disconnect();
  }
  if (autoplay.timer.value) {
    clearInterval(autoplay.timer.value);
  }
});

onBeforeUpdate(() => {
  if (numberOfPages.value !== props.list.length) {
    autoplay.stop();
    init(props.list.length);
  }
});

defineExpose({
  change: navigation?.change,
  move: navigation?.move,
});
</script>

<style lang="scss" scoped>
@import '~@csgIcons/new/video-control-play-dark-medium';
@import '~@csgIcons/new/video-control-pause-dark-medium';
@import '~@core/csg/src/scss/components/button-icon-mixin.scss';
@include csg-button-icon;

$transition: transform 300ms cubic-bezier(0.1, 0.57, 0.1, 1);

.v-slideshow-content {
  position: relative;
  overflow: hidden;
}

.slideshow-item {
  visibility: hidden;
  list-style-type: none;
  flex: 0 0 auto;
  &.active {
    visibility: visible;
  }
}

.v-slideshow-wrapper {
  overflow: hidden;
  position: relative;
  touch-action: pan-y pinch-zoom;

  &.is-swiping {
    touch-action: none;
  }

  .v-scroller {
    -webkit-transition: $transition;
    -moz-transition: $transition;
    -ms-transition: $transition;
    -o-transition: $transition;
    transition: $transition;

    .v-slideshow {
      flex-wrap: nowrap;
    }
  }

  @include breakpoint(small only) {
    .slideshow-item {
      visibility: visible;
      position: relative;
      background-color: #f8f8f8;
      max-height: fit-content;
    }
  }
}
</style>
